import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface HeaderNavigationProps {
  scrolled?: boolean;
}

const HeaderNavigation: React.FC<HeaderNavigationProps> = ({ scrolled = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [communityDropdownOpen, setCommunityDropdownOpen] = useState(false);
  const communityButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0 });
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debug logging
  console.log('Community dropdown state:', communityDropdownOpen);

  // Determine active tab based on current route
  const getActiveTab = () => {
    const pathname = location.pathname;
    if (pathname === '/') return 'home';
    if (pathname === '/designers') return 'designers';
    if (pathname === '/discover') return 'explore';
    if (pathname === '/pricing') return 'pricing';
    return 'home';
  };

  const handleTabChange = (value: string) => {
    switch (value) {
      case 'home':
        navigate('/');
        break;
      case 'explore':
        navigate('/discover');
        break;
      case 'designers':
        navigate('/designers');
        break;
      case 'pricing':
        navigate('/get-verified#pricing');
        break;
      default:
        navigate('/');
    }
  };

  return (
    <nav className="hidden md:flex items-center relative">
      <Tabs value={getActiveTab()} onValueChange={handleTabChange} className="w-auto">
        <TabsList className={`inline-flex items-center border border-zinc-200 rounded-full h-10 transition-all duration-300 ${
          scrolled ? 'bg-white' : 'bg-white/60'
        }`}>
          <TabsTrigger
            value="home"
            className="px-4 py-1.5 text-xs rounded-full transition-all focus:outline-none
              data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-medium
              data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
          >
            Home
          </TabsTrigger>
          <TabsTrigger
            value="explore"
            className="px-4 py-1.5 text-xs rounded-full transition-all focus:outline-none
              data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-medium
              data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
          >
            Explore
          </TabsTrigger>
          <TabsTrigger
            value="designers"
            className="px-4 py-1.5 text-xs rounded-full transition-all focus:outline-none
              data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-medium
              data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
          >
            Designers
          </TabsTrigger>

          <button
            ref={communityButtonRef}
            className="px-4 py-1.5 text-xs rounded-full transition-all focus:outline-none
              bg-transparent text-[#8A898C] font-medium hover:text-gray-800 flex items-center space-x-1"
            onMouseEnter={() => {
              if (dropdownTimeoutRef.current) {
                clearTimeout(dropdownTimeoutRef.current);
              }
              if (communityButtonRef.current) {
                const rect = communityButtonRef.current.getBoundingClientRect();
                const navRect = communityButtonRef.current.closest('nav')?.getBoundingClientRect();
                if (navRect) {
                  setDropdownPosition({ left: rect.left - navRect.left });
                }
              }
              setCommunityDropdownOpen(true);
            }}
            onMouseLeave={() => {
              dropdownTimeoutRef.current = setTimeout(() => {
                setCommunityDropdownOpen(false);
              }, 100);
            }}
          >
            <span>Community</span>
            <ChevronDown className="h-3 w-3" />
          </button>
          <TabsTrigger
            value="pricing"
            className="px-4 py-1.5 text-xs rounded-full transition-all focus:outline-none
              data-[state=active]:bg-[#E5E5E5] data-[state=active]:text-black data-[state=active]:font-medium
              data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8A898C] data-[state=inactive]:font-medium"
          >
            Pricing
          </TabsTrigger>
        </TabsList>
      </Tabs>
      
      {/* Dropdown menu - positioned outside TabsList */}
      {communityDropdownOpen && (
        <div 
          className="absolute top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-[9999]"
          style={{ left: `${dropdownPosition.left}px` }}
          onMouseEnter={() => {
            if (dropdownTimeoutRef.current) {
              clearTimeout(dropdownTimeoutRef.current);
            }
            setCommunityDropdownOpen(true);
          }}
          onMouseLeave={() => {
            dropdownTimeoutRef.current = setTimeout(() => {
              setCommunityDropdownOpen(false);
            }, 100);
          }}
        >
          <div className="pt-3 pb-4">
            <a 
              href="https://chat.whatsapp.com/GxGCQpa8S6l0E3i5dKZGQM?mode=ac_t" 
              target="_blank"
              rel="noopener noreferrer"
              className="block px-6 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              WCD Global
            </a>
            <a 
              href="https://chat.whatsapp.com/FEGXfBQSxSZBYiZqsCeKvP?mode=ac_t" 
              target="_blank"
              rel="noopener noreferrer"
              className="block px-6 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              WCD Paris
            </a>
            <a 
              href="https://chat.whatsapp.com/Gp3jN7g9fNQJg0eOSiw4r8?mode=ac_t" 
              target="_blank"
              rel="noopener noreferrer"
              className="block px-6 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              WCD Montpellier
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default HeaderNavigation; 